import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUp, Users, MapPin, Target, Recycle, Leaf, Flame, Wrench, LucideIcon } from 'lucide-react';
import { useJsonData } from '../../hooks/useJsonData';
import { useSafeAnimation, useSafeVariants } from '../ui/SafeMotion';

const iconMap: Record<string, LucideIcon> = {
  TrendingUp, Users, MapPin, Target, Recycle, Leaf, Flame, Wrench
};

interface StatItem {
  icon: string;
  value: number;
  suffix: string;
  label: string;
  description: string;
}

interface ImpactItem {
  icon: string;
  text: string;
  color: string;
}

interface ImpactData {
  stats: StatItem[];
  impacts: ImpactItem[];
}

// Optimized animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

function formatNumber(num: number): string {
  if (num >= 100000) return (num / 1000).toFixed(0) + 'K';
  return num.toLocaleString();
}

function AnimatedCounter({ value, suffix }: { value: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return <span ref={ref} className="tabular-nums">{formatNumber(count)}{suffix}</span>;
}

export function Impact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { data, loading } = useJsonData<ImpactData>('/data/impact.json');

  // SSR-safe animation props
  const headerAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 },
    isInView
  );
  const badgeAnim = useSafeAnimation(
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 },
    isInView
  );
  const impactAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 },
    isInView
  );
  const variants = useSafeVariants(isInView);

  const stats = data?.stats || [];
  const impacts = data?.impacts || [];

  return (
    <section ref={ref} id="impact" className="py-20 lg:py-28 bg-eco-green-800 text-white overflow-hidden relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
      </div>

      {/* Floating Orbs */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-eco-green-600/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-eco-green-700/30 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={headerAnim.initial}
          animate={headerAnim.animate}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={badgeAnim.initial}
            animate={badgeAnim.animate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-xl text-eco-green-200 text-sm font-semibold rounded-full mb-6 border border-white/20"
          >
            <TrendingUp className="w-4 h-4" />
            Measured Environmental Impact
          </motion.span>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            Impact Snapshot
          </h2>
          <p className="text-eco-green-200 max-w-xl mx-auto text-lg">
            Every ton recovered reduces landfill, saves resources, and lowers emissions.
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white/10 rounded-2xl p-6 animate-pulse">
                <div className="w-14 h-14 bg-white/20 rounded-xl mx-auto mb-4" />
                <div className="h-10 bg-white/20 rounded w-3/4 mx-auto mb-2" />
                <div className="h-4 bg-white/20 rounded w-1/2 mx-auto" />
              </div>
            ))}
          </div>
        )}

        {/* Stats Grid */}
        {!loading && (
          <motion.div
            variants={containerVariants}
            initial={variants.initial}
            animate={variants.animate}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12"
          >
            {stats.map((stat) => {
              const IconComponent = iconMap[stat.icon] || TrendingUp;
              return (
                <motion.div
                  key={stat.label}
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 text-center border border-white/20 shadow-xl shadow-black/10 hover:border-white/30 hover:bg-white/15 transition-all"
                >
                  <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-eco-green-400 to-eco-green-600 rounded-xl flex items-center justify-center shadow-lg">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-4xl lg:text-5xl font-bold text-white mb-1">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                  </div>
                  <p className="text-white font-medium">{stat.label}</p>
                  <p className="text-sm text-eco-green-300">{stat.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Impact Points */}
        {!loading && (
          <motion.div
            initial={impactAnim.initial}
            animate={impactAnim.animate}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/8 backdrop-blur-xl rounded-2xl p-6 border border-white/15 shadow-2xl"
          >
            <h3 className="text-center text-lg font-semibold text-white mb-6">Environmental Outcomes</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {impacts.map((impact) => {
                const IconComponent = iconMap[impact.icon] || Recycle;
                return (
                  <motion.div
                    key={impact.text}
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-xl p-4 hover:bg-white/10 transition-all cursor-default"
                  >
                    <div className={`${impact.color} p-2.5 rounded-lg`}>
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-sm text-eco-green-100 font-medium">{impact.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
